import React, { useState, useEffect, useRef } from 'react';
import { getStaticContent } from '../constants/staticContent';
import { FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const StarRating = ({ rating }) => (
  <div className="flex gap-1.5">
    {[1, 2, 3, 4, 5].map(i => (
      <FaStar key={i} size={18} className={i <= rating ? 'text-yellow-400' : 'text-gray-200'} />
    ))}
  </div>
);

const TestimonialsSection = ({ language = 'en' }) => {
  const { sectionLabel, sectionTitle, testimonials } = getStaticContent(language).TESTIMONIALS_CONTENT;
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = testimonials.length;
  const intervalRef = useRef(null);

  const next = () => setCurrent(c => (c + 1) % total);
  const prev = () => setCurrent(c => (c - 1 + total) % total);

  useEffect(() => {
    if (!paused) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      intervalRef.current = setInterval(() => setCurrent(c => (c + 1) % total), 3000);
    }
    return () => clearInterval(intervalRef.current);
  }, [paused, total]);

  // Show 3 cards on lg, 2 on sm, 1 on mobile — slice with wrap-around
  const getVisible = (count) =>
    Array.from({ length: count }, (_, i) => ({
      data: testimonials[(current + i) % total],
      key: `${current}-${i}`
    }));

  return (
    <section
      id="testimonials"
      className="bg-white py-20 px-4 md:px-8"
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-block mb-4">
            <span
              className="text-xs font-bold tracking-widest uppercase text-emerald-700 bg-emerald-50 px-4 py-1.5 rounded-full border border-emerald-200"
            >
              {sectionLabel}
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
            {sectionTitle}
          </h2>
        </div>

        {/* Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Cards — responsive visibility via CSS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden py-4">
            {getVisible(3).map(({ data: t, key }, i) => (
              <div
                key={key}
                className={`relative bg-white rounded-2xl flex flex-col gap-5
                            transition-all duration-500
                            ${i === 1 ? 'hidden sm:flex' : ''} ${i === 2 ? 'hidden lg:flex' : 'flex'}`}
                style={{
                  padding: '28px 32px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)'
                }}
              >
                {/* Decorative Quote Mark */}
                <div
                  className="absolute top-4 left-6"
                  style={{
                    color: '#BBF7D0',
                    fontSize: '64px',
                    opacity: 0.6,
                    fontFamily: 'serif',
                    lineHeight: 1
                  }}
                >
                  &ldquo;
                </div>

                <div className="relative z-10 flex flex-col gap-5 h-full pt-4">
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed flex-1">
                    {t.review}
                  </p>
                  <StarRating rating={t.rating} />
                  <div className="flex items-center gap-4 pt-4 border-t border-gray-100 mt-2">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full border border-gray-200 bg-gray-50 object-cover"
                      onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=d1fae5&color=065f46`; }}
                    />
                    <div>
                      <p className="font-bold text-gray-900 text-base">{t.name}</p>
                      <p className="text-sm text-gray-500">{t.location}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Prev / Next arrows */}
          <button
            onClick={() => { prev(); setPaused(true); setTimeout(() => setPaused(false), 5000); }}
            className="absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-gray-200
                       flex items-center justify-center text-emerald-600 shadow-lg
                       hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all duration-200 z-10"
          >
            <FaChevronLeft size={14} />
          </button>
          <button
            onClick={() => { next(); setPaused(true); setTimeout(() => setPaused(false), 5000); }}
            className="absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-gray-200
                       flex items-center justify-center text-emerald-600 shadow-lg
                       hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all duration-200 z-10"
          >
            <FaChevronRight size={14} />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-2 mt-10">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => { setCurrent(i); setPaused(true); setTimeout(() => setPaused(false), 5000); }}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === current ? 'bg-emerald-600 w-8' : 'bg-gray-300 w-2.5'
              }`}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default TestimonialsSection;
